import React, { Component } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown, Check, MessageCircle } from 'lucide-react';
import { Button } from './ui';
const WHATSAPP_URL = 'https://wa.me/5491122836110?text=' + encodeURIComponent('Hola! Me interesa el viaje a Fernando de Noronha 2026.');
function scrollToPacks() {
  document.getElementById('elegir-pack')?.scrollIntoView({
    behavior: 'smooth'
  });
}
const fade = (delay: number) => ({
  initial: {
    opacity: 0,
    y: 20
  },
  animate: {
    opacity: 1,
    y: 0
  },
  transition: {
    duration: 0.7,
    delay,
    ease: 'easeOut' as const
  }
});
export function Hero() {
  return <section className="relative min-h-screen flex items-center overflow-hidden bg-noronha-volcanic" data-id="element-279">
      {/* Clean minimal background — subtle vignette only, no media */}
      <div className="absolute inset-0 z-0 pointer-events-none" data-id="element-280">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_30%,rgba(31,163,122,0.07)_0%,transparent_60%)]" data-id="element-281" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_100%,rgba(14,59,51,0.4)_0%,transparent_70%)]" data-id="element-282" />
      </div>

      <div className="container relative z-10 mx-auto px-6 pt-36 pb-[280px] sm:pt-44 sm:pb-[260px] md:pt-48 md:pb-[300px]" data-id="element-283">
        <div className="max-w-2xl mx-auto flex flex-col items-center text-center" data-id="element-284">
          {/* Refined exclusive label badge */}
          <motion.div {...fade(0.05)} className="mb-4 md:mb-6 inline-flex items-center gap-2 px-3.5 py-1 rounded-full border border-noronha-emerald/40 bg-noronha-emerald/5" data-id="element-285">
            <Check className="w-3.5 h-3.5 text-noronha-emerald" strokeWidth={3} data-id="element-286" />
            <span className="font-heading font-bold text-[11px] sm:text-xs tracking-[0.08em] text-noronha-emerald uppercase" data-id="element-287">
              Viaje grupal exclusivo
            </span>
          </motion.div>

          {/* Title — stacked, NORONHA as the dominant word */}
          <motion.h1 {...fade(0.15)} className="mb-3 md:mb-6 font-heading font-bold text-noronha-text-heading uppercase leading-[0.88] tracking-tight flex flex-col items-center" data-id="element-288">
            <span className="text-2xl sm:text-3xl md:text-4xl tracking-[0.18em] text-noronha-text-body font-semibold" data-id="element-289">
              Fernando
            </span>
            <span className="text-base sm:text-xl md:text-2xl tracking-[0.3em] text-noronha-text-body/70 font-medium" data-id="element-290">
              De
            </span>
            <span className="text-5xl sm:text-7xl md:text-8xl lg:text-9xl tracking-tight" data-id="element-291">
              Noronha
            </span>
            <span className="text-4xl sm:text-6xl md:text-7xl lg:text-8xl text-noronha-emerald" data-id="element-292">
              2026
            </span>
          </motion.h1>

          {/* Subtitle */}
          <motion.p {...fade(0.3)} className="text-base sm:text-xl md:text-2xl text-noronha-text-body max-w-[20rem] sm:max-w-xl mb-1.5" data-id="element-293">
            Una semana de buceo, naturaleza y aventura en uno de los destinos
            más exóticos del planeta.
          </motion.p>

          {/* Highlight */}
          <motion.p {...fade(0.4)} className="text-base sm:text-lg md:text-xl text-noronha-sand font-medium italic mb-4 md:mb-6" data-id="element-294">
            Donde la naturaleza es la protagonista.
          </motion.p>

          {/* Strategic info block */}
          <motion.div {...fade(0.5)} className="mb-5 md:mb-8 flex flex-col gap-1.5 md:gap-2 items-center" data-id="element-295">
            <p className="font-heading font-semibold text-sm md:text-lg tracking-wide text-noronha-emerald uppercase" data-id="element-296">
              Viaje grupal exclusivo organizado por Buceo y Navegación
            </p>
            <p className="text-sm md:text-lg text-noronha-text-body" data-id="element-297">
              17 al 24 de noviembre de 2026
            </p>
          </motion.div>

          {/* CTAs — stacked, full width (Bonaire structure) */}
          <motion.div {...fade(0.6)} className="flex flex-col gap-3 w-full max-w-md" data-id="element-298">
            <Button variant="primary" onClick={scrollToPacks} className="w-full justify-center text-base px-8 py-4" data-id="element-299">
              <span className="text-lg leading-none" data-id="element-300">🤿</span>
              Quiero sumarme al grupo
            </Button>
            <Button variant="secondary" onClick={() => window.open(WHATSAPP_URL, '_blank', 'noopener')} className="w-full justify-center text-base px-8 py-4" data-id="element-301">
              <MessageCircle className="w-5 h-5" strokeWidth={1.75} data-id="element-302" />
              Hablar por WhatsApp
            </Button>
          </motion.div>

          {/* Availability indicator */}
          <motion.div {...fade(0.7)} className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-noronha-gold/15 border border-noronha-gold/50" data-id="element-303">
            <span className="text-base leading-none" data-id="element-304">🔥</span>
            <span className="font-heading font-bold text-sm sm:text-base tracking-[0.12em] text-noronha-text-heading uppercase whitespace-nowrap" data-id="element-305">
              Quedan 6 lugares disponibles
            </span>
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator — sits above the transition glow */}
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 1.2,
      duration: 1
    }} className="absolute bottom-[360px] left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-noronha-text-body/50 z-20 hidden sm:flex" data-id="element-306">
        <span className="font-mono text-[10px] uppercase tracking-[0.2em]" data-id="element-307">
          Descubrir
        </span>
        <motion.div animate={{
        y: [0, 8, 0]
      }} transition={{
        repeat: Infinity,
        duration: 2,
        ease: 'easeInOut'
      }} data-id="element-308">
          <ChevronDown className="w-5 h-5" data-id="element-309" />
        </motion.div>
      </motion.div>

      {/* Seamless transition into the bright editorial section below — emerging from the depths into light.
                  Sits BEHIND the hero content (z-0) so it never covers the CTAs or availability pill. */}
      <div aria-hidden="true" className="absolute bottom-0 left-0 right-0 h-[320px] z-0 pointer-events-none" style={{
      background: 'linear-gradient(to bottom, rgba(2,6,23,0) 0%, #020617 45%, #0A1F2D 78%, #FFFFFF 100%)'
    }} data-id="element-310" />
    </section>;
}